import { useCustomAlert } from "../../alerts";
import useSWRFetcher, { SWRFetchError } from "../fetcher";
import useSWR from "swr";
import settings from "../../../../constants/settings";
import { useCallback, useEffect, useState } from "react";
import { SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN_LATER } from "../../../../constants/errors";
import { UserSummary } from "../../../../reducers/models/users";
import { FormikHelpers } from "formik/dist/types";
import useFetchAndTrace from "../../fetchWithTrace";

interface CollaboratorsResponse {
  collaborators: UserSummary[];
}

export interface AddCollaboratorFormValues {
  email: string;
}

export default function useBoardCollaborators(boardId: string) {
  const customAlert = useCustomAlert();
  const fetcher = useSWRFetcher<CollaboratorsResponse>(undefined);
  const fetchAndTrace = useFetchAndTrace();
  const [updating, setUpdating] = useState(false);

  const { data, mutate, error } = useSWR<CollaboratorsResponse, SWRFetchError>(
    `${settings.api_url}/boards/${boardId}/collaborators`,
    fetcher,
  );

  const onError = useCallback(
    (detail?: string) => {
      setUpdating(false);
      customAlert(detail || SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN_LATER);
    },
    [customAlert],
  );

  useEffect(() => {
    if (error !== undefined) {
      onError();
    }
  }, [error, onError]);

  const addCollaboratorHandler = (resetForm: () => void) => {
    return async (response: Response) => {
      setUpdating(false);
      if (response.ok) {
        const collaboratorsResponse =
          (await response.json()) as CollaboratorsResponse;
        void mutate(collaboratorsResponse);
        resetForm();
      } else if (response.status === 400 || response.status === 403) {
        const { detail } = (await response.json()) as { detail: string };
        onError(detail);
      } else {
        throw Error("Failed to add collaborator");
      }
    };
  };

  const errorHandler = () => {
    onError();
  };

  const addCollaborator = (
    formValues: AddCollaboratorFormValues,
    formikHelpers: FormikHelpers<AddCollaboratorFormValues>,
  ) => {
    setUpdating(true);
    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}/collaborators`,
      addCollaboratorHandler(formikHelpers.resetForm),
      errorHandler,
      {
        body: JSON.stringify({
          email: formValues.email,
        }),
        method: "PUT",
      },
    );
  };

  const removeCollaboratorHandler = async (response: Response) => {
    setUpdating(false);
    if (response.ok) {
      const collaboratorsResponse =
        (await response.json()) as CollaboratorsResponse;
      void mutate(collaboratorsResponse);
    } else if (response.status === 400 || response.status === 403) {
      const { detail } = (await response.json()) as { detail: string };
      onError(detail);
    } else {
      throw Error("Failed to add collaborator");
    }
  };

  const removeCollaborator = (collaborator: UserSummary) => {
    setUpdating(true);
    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}/collaborators/remove`,
      removeCollaboratorHandler,
      errorHandler,
      {
        body: JSON.stringify({
          user_id: collaborator.id,
        }),
        method: "PUT",
      },
    );
  };

  return {
    collaborators: data !== undefined ? data.collaborators : null,
    addCollaborator,
    removeCollaborator,
    updating,
  };
}
